import EmptyState from "@components/invoiceForm";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./index.scss";
import Loader from "@components/Loader/Loader";
import ReactPaginate from "react-paginate";
import { useSelector } from "react-redux";
import { useGetClientsQuery } from "store/client/clientApi";
import { useGetInvoicesQuery } from "store/invoice/invoiceApi";
import InvoiceList from "./InvoiceList";

const InvoiceListPage = () => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState("all");
  const currentUser = useSelector((state) => state.auth.user);

  const { data: invoices, isLoading } = useGetInvoicesQuery(
    currentUser?.companyId?._id
  );

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [clientNameFilter, setClientNameFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const normalizedCompanyId =
    typeof currentUser?.companyId === "object"
      ? currentUser.companyId._id
      : currentUser?.companyId;
  const { data: clientsData, isLoading: isClientsLoading } = useGetClientsQuery(
    normalizedCompanyId,
    {
      skip: !normalizedCompanyId,
    }
  );

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  // Calculate counts for each tab
  const invoiceCounts = {
    all: invoices?.data?.length || 0,
    brouillon:
      invoices?.data?.filter(
        (inv) => inv.invoiceStatus?.toLowerCase() === "brouillon"
      ).length || 0,
    "A valider":
      invoices?.data?.filter(
        (inv) => inv.invoiceStatus?.toLowerCase() === "a valider"
      ).length || 0,
    validé:
      invoices?.data?.filter(
        (inv) => inv.invoiceStatus?.toLowerCase() === "validé"
      ).length || 0,
    publié:
      invoices?.data?.filter(
        (inv) => inv.invoiceStatus?.toLowerCase() === "publié"
      ).length || 0,
  };

  // Advanced search dropdown state
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchFields, setSearchFields] = useState({
    numeroInvoice: "",
    clientName: "",
    totalHT: "",
    paymentStatus: "",
    montantMin: "",
    montantMax: "",
  });

  // This flag is used to trigger filtering by advanced search only when "Appliquer" is clicked
  const [advancedSearchActive, setAdvancedSearchActive] = useState(false);

  const handleAdvancedSearchChange = (e) => {
    console.log("e.target.name", e.target.name);
    console.log("e.target.value", e.target.value);

    setSearchFields({ ...searchFields, [e.target.name]: e.target.value });
  };

  const handleAdvancedSearchReset = () => {
    setSearchFields({
      numeroInvoice: "",
      clientName: "",
      totalHT: "",
      paymentStatus: "",
      montantMin: "",
      montantMax: "",
    });
    setAdvancedSearchActive(false); // Reset aussi le flag de recherche avancée
  };

  const handleAdvancedSearchApply = () => {
    setShowAdvancedSearch(false);
    setAdvancedSearchActive(true);
    setSearchValue(""); // Optionally clear the basic search input
  };

  // Reset advanced search when changing tab, date, or client
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setAdvancedSearchActive(false);
  };
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setAdvancedSearchActive(false);
  };
  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setAdvancedSearchActive(false);
  };
  const handleClientChange = (e) => {
    setClientNameFilter(e.target.value);
    setAdvancedSearchActive(false);
  };

  const filteredInvoices = invoices?.data
    ?.filter((invoice) => {
      if (selectedTab === "all") return true;
      return (
        invoice.invoiceStatus &&
        invoice.invoiceStatus.toLowerCase() === selectedTab.toLowerCase()
      );
    })
    ?.filter((invoice) => {
      if (!startDate && !endDate) return true;

      // CORRECTION: Utiliser dateDeFacturation au lieu de invoiceDate
      const invoiceDate = new Date(invoice.dateDeFacturation);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      // Réinitialiser l'heure pour la comparaison de dates seulement
      if (start) start.setHours(0, 0, 0, 0);
      if (end) end.setHours(23, 59, 59, 999);
      invoiceDate.setHours(12, 0, 0, 0); // Heure du milieu de journée pour éviter les problèmes de fuseau horaire

      if (start && invoiceDate < start) return false;
      if (end && invoiceDate > end) return false;
      return true;
    })
    ?.filter((invoice) => {
      if (!clientNameFilter) return true;

      // CORRECTION: Gérer clientId qui peut être un objet ou un string
      const clientId =
        typeof invoice.clientId === "object"
          ? invoice.clientId?._id
          : invoice.clientId;

      return clientId === clientNameFilter;
    })
    ?.filter((invoice) => {
      // If advanced search is active, skip the basic searchValue filter
      if (advancedSearchActive) return true;
      if (!searchValue) return true;
      const search = searchValue.toLowerCase();
      return (
        (invoice.numeroInvoice &&
          invoice.numeroInvoice.toLowerCase().includes(search)) ||
        (invoice.clientName &&
          invoice.clientName.toLowerCase().includes(search)) ||
        (invoice.client?.name &&
          invoice.client?.name.toLowerCase().includes(search)) ||
        (invoice.totalHT &&
          String(invoice.totalHT).toLowerCase().includes(search)) ||
        (invoice.paymentStatus &&
          invoice.paymentStatus.toLowerCase().includes(search))
      );
    })
    ?.filter((invoice) => {
      // Only apply advanced search if active
      if (!advancedSearchActive) return true;

      // Vérifier si au moins un champ de recherche avancée est rempli
      const hasAdvancedSearchCriteria =
        searchFields.numeroInvoice ||
        searchFields.clientName ||
        searchFields.totalHT ||
        searchFields.paymentStatus ||
        searchFields.montantMin ||
        searchFields.montantMax;

      if (!hasAdvancedSearchCriteria) return true;

      // Filtre par numéro de facture
      if (
        searchFields.numeroInvoice &&
        !(
          invoice.numeroInvoice &&
          invoice.numeroInvoice
            .toLowerCase()
            .includes(searchFields.numeroInvoice.toLowerCase())
        )
      ) {
        return false;
      }

      // Filtre par nom du client
      if (
        searchFields.clientName &&
        !(
          (invoice.clientName &&
            invoice.clientName
              .toLowerCase()
              .includes(searchFields.clientName.toLowerCase())) ||
          (invoice.client?.name &&
            invoice.client?.name
              .toLowerCase()
              .includes(searchFields.clientName.toLowerCase()))
        )
      ) {
        return false;
      }

      // Filtre par total HT exact (si spécifié)
      if (
        searchFields.totalHT &&
        String(invoice.totalHT) !== String(searchFields.totalHT)
      ) {
        return false;
      }

      // CORRECTION: Filtre par statut de paiement - recherche EXACTE
      if (
        searchFields.paymentStatus &&
        invoice.paymentStatus !== searchFields.paymentStatus
      ) {
        return false;
      }

      // Filtre par montant minimum
      if (
        searchFields.montantMin &&
        Number(invoice.totalHT) < Number(searchFields.montantMin)
      ) {
        return false;
      }

      // Filtre par montant maximum
      if (
        searchFields.montantMax &&
        Number(invoice.totalHT) > Number(searchFields.montantMax)
      ) {
        return false;
      }

      return true;
    });

  // Pagination logic
  const pageCount = Math.ceil((filteredInvoices?.length || 0) / itemsPerPage);
  const paginatedInvoices = filteredInvoices?.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );
  console.log("paginatedInvoices", paginatedInvoices);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  // Ajouter un bouton pour effacer tous les filtres
  const handleClearAllFilters = () => {
    setStartDate("");
    setEndDate("");
    setClientNameFilter("");
    setSearchValue("");
    setAdvancedSearchActive(false);
    handleAdvancedSearchReset();
    setCurrentPage(0);
  };

  return (
    <div className="invoice-layout d-flex" id="invoice-list-page">
      <div className="invoice-page p-3 flex-grow-1">
        <header className="invoice-header d-flex justify-between align-items-center">
          <h1 className="p-3">{t("invoice.title")}</h1>
          <div
            style={{
              display: "flex",
              gap: 0,
              alignItems: "center",
              position: "relative",
            }}>
            <input
              type="text"
              placeholder={t("invoice.search_placeholder")}
              className="invoice-search"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setAdvancedSearchActive(false); // Disable advanced search when using basic search
              }}
              style={{ paddingRight: 80 }}
            />
            <button
              type="button"
              className="btn-secondary"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                height: "70%",
                padding: "0 12px",
                fontSize: "0.95em",
                display: "flex",
                alignItems: "center",
                gap: 4,
                zIndex: 0,
              }}
              onClick={() => setShowAdvancedSearch((prev) => !prev)}>
              Avancé
              <span
                style={{
                  display: "inline-block",
                  marginLeft: 4,
                  fontSize: "0.8em", // smaller icon
                  transition: "transform 0.2s",
                  transform: showAdvancedSearch ? "rotate(180deg)" : "none",
                }}>
                ▼
              </span>
            </button>
            {/* Advanced Search Dropdown */}
            {showAdvancedSearch && (
              <div
                style={{
                  position: "absolute",
                  top: "110%",
                  right: 0,
                  background: "#fff",
                  boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
                  borderRadius: 8,
                  padding: 24,
                  minWidth: 320,
                  zIndex: 10,
                  width: "100%",
                }}>
                <h3 style={{ marginTop: 0, color: "#000" }}>
                  RECHERCHE AVANCÉE
                </h3>
                <div className="form-group">
                  <label style={{ color: "#000" }}>N° de facture</label>
                  <input
                    name="numeroInvoice"
                    value={searchFields.numeroInvoice}
                    onChange={handleAdvancedSearchChange}
                    className="table-input"
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#000" }}>Status de paiement</label>
                  <select
                    name="paymentStatus"
                    value={searchFields.paymentStatus}
                    onChange={handleAdvancedSearchChange}
                    className="table-input">
                    <option value="">Tous les statuts</option>
                    <option value="paid">
                      {t("invoice.paid_status") || "Payé"}
                    </option>
                    <option value="partially_paid">
                      {t("invoice.partially_paid") || "Partiellement payé"}
                    </option>
                    <option value="unpaid">
                      {t("invoice.unpaid") || "Non payé"}
                    </option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ color: "#000" }}>Montant min</label>
                  <input
                    name="montantMin"
                    value={searchFields.montantMin}
                    onChange={handleAdvancedSearchChange}
                    className="table-input"
                    type="number"
                  />
                </div>
                <div className="form-group">
                  <label style={{ color: "#000" }}>Montant max</label>
                  <input
                    name="montantMax"
                    value={searchFields.montantMax}
                    onChange={handleAdvancedSearchChange}
                    className="table-input"
                    type="number"
                  />
                </div>
                <div
                  className="form-actions"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleAdvancedSearchReset}>
                    Réinitialiser
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                      handleAdvancedSearchApply();
                      setShowAdvancedSearch(false);
                    }}>
                    Appliquer
                  </button>
                </div>
                <button
                  type="button"
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "none",
                    border: "none",
                    fontSize: 18,
                    cursor: "pointer",
                  }}
                  onClick={() => setShowAdvancedSearch(false)}
                  aria-label="Fermer">
                  ×
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Filtres et bouton de réinitialisation */}
        <div
          className="invoice-date-filter p-3"
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}>
          <label>
            {t("invoice.start_date") || "Date début"}
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
            />
          </label>
          <label>
            {t("invoice.end_date") || "Date fin"}
            <input type="date" value={endDate} onChange={handleEndDateChange} />
          </label>
          <label>
            {t("invoice.client_name") || "Client"}
            <select
              value={clientNameFilter}
              onChange={handleClientChange}
              disabled={isClientsLoading}
              className="form-select">
              <option value="">Tous les clients</option>
              {clientsData?.data?.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
          {(startDate ||
            endDate ||
            clientNameFilter ||
            searchValue ||
            advancedSearchActive) && (
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClearAllFilters}
              style={{ marginLeft: "auto", fontSize: "0.9em" }}>
              reset filtre
            </button>
          )}
        </div>

        <nav className="invoice-tabs p-3">
          <ul>
            <li
              className={selectedTab === "all" ? "active" : ""}
              onClick={() => handleTabChange("all")}>
              {t("invoice.all")}
              <span
                style={{
                  marginLeft: 8,
                  background: "#eee",
                  borderRadius: "1em",
                  padding: "2px 10px",
                  fontSize: "0.85em",
                  color: "#333",
                }}>
                {invoiceCounts.all}
              </span>
            </li>
            <li
              className={selectedTab === "brouillon" ? "active" : ""}
              onClick={() => handleTabChange("brouillon")}>
              {t("invoice.draft")}
              <span
                style={{
                  marginLeft: 8,
                  background: "#eee",
                  borderRadius: "1em",
                  padding: "2px 10px",
                  fontSize: "0.85em",
                  color: "#333",
                }}>
                {invoiceCounts.brouillon}
              </span>
            </li>
            <li
              className={selectedTab === "A valider" ? "active" : ""}
              onClick={() => handleTabChange("A valider")}>
              {t("invoice.finalized")}
              <span
                style={{
                  marginLeft: 8,
                  background: "#eee",
                  borderRadius: "1em",
                  padding: "2px 10px",
                  fontSize: "0.85em",
                  color: "#333",
                }}>
                {invoiceCounts["A valider"]}
              </span>
            </li>
            <li
              className={selectedTab === "validé" ? "active" : ""}
              onClick={() => handleTabChange("validé")}>
              {t("invoice.paid")}
              <span
                style={{
                  marginLeft: 8,
                  background: "#eee",
                  borderRadius: "1em",
                  padding: "2px 10px",
                  fontSize: "0.85em",
                  color: "#333",
                }}>
                {invoiceCounts.validé}
              </span>
            </li>
            <li
              className={selectedTab === "publié" ? "active" : ""}
              onClick={() => handleTabChange("publié")}>
              {t("invoice.to_pay")}
              <span
                style={{
                  marginLeft: 8,
                  background: "#eee",
                  borderRadius: "1em",
                  padding: "2px 10px",
                  fontSize: "0.85em",
                  color: "#333",
                }}>
                {invoiceCounts.publié}
              </span>
            </li>
          </ul>
        </nav>
        {isLoading ? (
          <Loader />
        ) : paginatedInvoices?.length > 0 ? (
          <>
            <InvoiceList invoices={paginatedInvoices} />
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 24,
              }}>
              <ReactPaginate
                previousLabel={"←"}
                nextLabel={"→"}
                breakLabel={"..."}
                pageCount={pageCount}
                marginPagesDisplayed={1}
                pageRangeDisplayed={2}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                activeClassName={"active"}
                forcePage={currentPage}
              />
            </div>
          </>
        ) : selectedTab === "all" ? (
          <EmptyState type="invoice" />
        ) : (
          <div
            style={{
              padding: "20vh 20px",
              width: "100%",
              textAlign: "center",
              color: "#777",
              fontSize: "1.2rem",
              fontWeight: "500",
              backgroundColor: "#f9f9f9",
              borderRadius: 12,
              boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              marginLeft: "auto",
              marginRight: "auto",
              userSelect: "none",
              letterSpacing: "0.03em",
            }}>
            {t("invoice.no_invoices_in_this_tab") ||
              "Aucune facture dans cet état."}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceListPage;
